(function () {
  const FISH_GUIDE_SNAPSHOT_SCHEMA = "fishystuff.fishydex.fish-guide-snapshot.v1";
  const FISH_GUIDE_SERVICE_REGIONS = Object.freeze([
    "NAEU",
    "TR",
    "NEZ",
    "ASIA",
    "SA",
    "JP",
    "KR",
    "TW",
    "GLOBAL_LAB",
  ]);
  const MAX_U16 = 65_535;
  const MAX_U32 = 4_294_967_295;
  const MAX_U64_DECIMAL = "18446744073709551615";
  const UNKNOWN_GUIDE_COUNT = Object.freeze({ kind: "unknown", count: null });
  const UNAVAILABLE_GUIDE_COUNT = Object.freeze({ kind: "unavailable", count: null });
  const NOT_APPLICABLE_GUIDE_COUNT = Object.freeze({ kind: "not_applicable", count: null });
  const ZERO_GUIDE_COUNT = Object.freeze({ kind: "known", count: 0 });

  function normalizeFishIds(values) {
    let ids = [];
    if (Array.isArray(values)) {
      ids = values;
    } else if (values && typeof values === "object") {
      ids = Object.entries(values)
        .filter(function (entry) {
          return entry[1];
        })
        .map(function (entry) {
          return entry[0];
        });
    }

    const unique = new Set();
    for (const raw of ids) {
      const fishId = Number.parseInt(String(raw), 10);
      if (Number.isInteger(fishId) && fishId > 0) {
        unique.add(fishId);
      }
    }
    return Array.from(unique).sort(function (left, right) {
      return left - right;
    });
  }

  function isPlainRecord(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return false;
    }
    const prototype = Object.getPrototypeOf(value);
    return prototype === null || Object.prototype.toString.call(value) === "[object Object]";
  }

  function hasExactKeys(value, keys) {
    if (!isPlainRecord(value)) {
      return false;
    }
    const actual = Object.keys(value).sort();
    const expected = keys.slice().sort();
    return actual.length === expected.length
      && actual.every(function (key, index) { return key === expected[index]; });
  }

  function isPositiveU64Decimal(value) {
    if (typeof value !== "string" || !/^[1-9][0-9]{0,19}$/.test(value)) {
      return false;
    }
    return value.length < MAX_U64_DECIMAL.length
      || (value.length === MAX_U64_DECIMAL.length && value <= MAX_U64_DECIMAL);
  }

  function isObservedAt(value) {
    if (typeof value !== "string") {
      return false;
    }
    const match = value.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,9})?Z$/,
    );
    if (!match) {
      return false;
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const hour = Number(match[4]);
    const minute = Number(match[5]);
    const second = Number(match[6]);
    const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    const daysInMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return year > 0
      && month >= 1
      && month <= 12
      && day >= 1
      && day <= daysInMonth[month - 1]
      && hour <= 23
      && minute <= 59
      && second <= 59;
  }

  function normalizeFishGuideSnapshot(value) {
    if (!hasExactKeys(value, [
      "schema",
      "subject",
      "observedAt",
      "client",
      "coverage",
      "entries",
    ])) {
      throw new TypeError("Fish Guide snapshot has unsupported or missing fields");
    }
    if (value.schema !== FISH_GUIDE_SNAPSHOT_SCHEMA) {
      throw new TypeError("Unsupported Fish Guide snapshot schema");
    }
    if (!hasExactKeys(value.subject, ["serviceRegion", "familyId"])) {
      throw new TypeError("Fish Guide snapshot subject is invalid");
    }
    if (!FISH_GUIDE_SERVICE_REGIONS.includes(value.subject.serviceRegion)) {
      throw new TypeError("Fish Guide snapshot service region is invalid");
    }
    if (!isPositiveU64Decimal(value.subject.familyId)) {
      throw new TypeError("Fish Guide snapshot family ID is invalid");
    }
    if (!isObservedAt(value.observedAt)) {
      throw new TypeError("Fish Guide snapshot observation time is invalid");
    }
    if (!hasExactKeys(value.client, ["buildId", "sha256"])) {
      throw new TypeError("Fish Guide snapshot client identity is invalid");
    }
    if (typeof value.client.buildId !== "string" || !/^[1-9][0-9]{0,19}$/.test(value.client.buildId)) {
      throw new TypeError("Fish Guide snapshot client build ID is invalid");
    }
    if (typeof value.client.sha256 !== "string" || !/^[0-9a-f]{64}$/.test(value.client.sha256)) {
      throw new TypeError("Fish Guide snapshot client SHA-256 is invalid");
    }
    if (value.coverage !== "complete" && value.coverage !== "partial") {
      throw new TypeError("Fish Guide snapshot coverage is invalid");
    }
    if (!Array.isArray(value.entries)) {
      throw new TypeError("Fish Guide snapshot entries are invalid");
    }

    const seenKeys = new Set();
    const entries = value.entries.map(function (entry) {
      if (!hasExactKeys(entry, ["encyclopediaKey", "catchCount"])) {
        throw new TypeError("Fish Guide snapshot entry has unsupported or missing fields");
      }
      if (!Number.isInteger(entry.encyclopediaKey)
          || entry.encyclopediaKey <= 0
          || entry.encyclopediaKey > MAX_U16) {
        throw new TypeError("Fish Guide snapshot encyclopedia key is invalid");
      }
      if (!Number.isInteger(entry.catchCount)
          || entry.catchCount < 0
          || entry.catchCount > MAX_U32) {
        throw new TypeError("Fish Guide snapshot catch count is invalid");
      }
      if (seenKeys.has(entry.encyclopediaKey)) {
        throw new TypeError("Fish Guide snapshot contains a duplicate encyclopedia key");
      }
      seenKeys.add(entry.encyclopediaKey);
      return {
        encyclopediaKey: entry.encyclopediaKey,
        catchCount: entry.catchCount,
      };
    }).sort(function (left, right) {
      return left.encyclopediaKey - right.encyclopediaKey;
    });

    return {
      schema: FISH_GUIDE_SNAPSHOT_SCHEMA,
      subject: {
        serviceRegion: value.subject.serviceRegion,
        familyId: value.subject.familyId,
      },
      observedAt: value.observedAt,
      client: {
        buildId: value.client.buildId,
        sha256: value.client.sha256,
      },
      coverage: value.coverage,
      entries,
    };
  }

  function parseFishGuideSnapshot(raw) {
    return normalizeFishGuideSnapshot(JSON.parse(raw));
  }

  function normalizeFishGuideProgress(value) {
    if (!value || typeof value !== "object") {
      return { status: "unknown", snapshot: null };
    }
    if (value.status === "available") {
      try {
        return {
          status: "available",
          snapshot: normalizeFishGuideSnapshot(value.snapshot),
        };
      } catch (_error) {
        return { status: "corrupted", snapshot: null };
      }
    }
    if (value.status === "unavailable" || value.status === "corrupted") {
      return { status: value.status, snapshot: null };
    }
    return { status: "unknown", snapshot: null };
  }

  function createFishGuideCountLookup(progress) {
    const normalizedProgress = normalizeFishGuideProgress(progress);
    const snapshot = normalizedProgress.snapshot;
    const fallback = normalizedProgress.status === "unavailable"
      ? UNAVAILABLE_GUIDE_COUNT
      : snapshot?.coverage === "complete" ? ZERO_GUIDE_COUNT : UNKNOWN_GUIDE_COUNT;
    const counts = new Map();
    if (snapshot) {
      for (const entry of snapshot.entries) {
        counts.set(entry.encyclopediaKey, entry.catchCount === 0
          ? ZERO_GUIDE_COUNT
          : Object.freeze({ kind: "known", count: entry.catchCount }));
      }
    }
    return function (encyclopediaKey) {
      if (!Number.isInteger(encyclopediaKey) || encyclopediaKey <= 0 || encyclopediaKey > MAX_U16) {
        return NOT_APPLICABLE_GUIDE_COUNT;
      }
      return counts.get(encyclopediaKey) ?? fallback;
    };
  }



  window.__fishystuffSharedFishState = Object.freeze({
    FISH_GUIDE_SNAPSHOT_SCHEMA: FISH_GUIDE_SNAPSHOT_SCHEMA,
    normalizeIds: normalizeFishIds,
    normalizeFishGuideSnapshot: normalizeFishGuideSnapshot,
    parseFishGuideSnapshot: parseFishGuideSnapshot,
    normalizeFishGuideProgress: normalizeFishGuideProgress,
    createFishGuideCountLookup: createFishGuideCountLookup,
  });
})();
